import { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { Save, FileText, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import { SiteConfigClient } from '../../../infrastructure/SiteConfigClient';

const SITE_CONFIG_KEY = 'customs-product-name-prompt-examples';

// Mirrors DEFAULT_CUSTOMS_PRODUCT_NAME_PROMPT_CONFIG in the facade; shown as a starting template
// when the site config entry does not exist yet.
const DEFAULT_CONFIG = {
  examples: [
    {
      productName: 'Relief Sun Probiotics SPF 50 PA, 50ml 50ml',
      category: ['Sunscreen'],
      customsName: 'Sunblock Lotion',
    },
    {
      productName: 'Ink Lasting Stick Foundation SPF50 PA, 15g, 203 Beige, 1pack',
      category: ['Foundation'],
      customsName: 'Makeup Foundation',
    },
    {
      productName: 'PDRN Hyaluronic Acid 100 Moisture Cream, 60ml',
      category: ['Face Cream'],
      customsName: 'Moisture Cream',
    },
    { productName: 'PDRN Pink Gel Mask 4ea', category: ['Sheet Mask'], customsName: 'Facial Sheet Mask' },
    { productName: 'Eye Patch Jericho Rose, 60ea', category: ['Eye Mask'], customsName: 'Hydrogel Eye Patch' },
    { productName: 'Whitening Toothpaste', category: ['Oral Care'], customsName: 'Dental Cleaner' },
    { productName: 'Green Tea Foam Cleanser 150ml', category: ['Cleanser'], customsName: 'Facial Foam Cleanser' },
    { productName: 'Vitamin C Brightening Serum 30ml', category: ['Serum'], customsName: 'Facial Serum' },
  ],
};

const validateConfig = (parsed: unknown): string | null => {
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return 'Config must be a JSON object like { "examples": [...] }';
  }
  const examples = (parsed as Record<string, unknown>)['examples'];
  if (!Array.isArray(examples) || examples.length === 0) {
    return '"examples" must be a non-empty array';
  }
  for (let i = 0; i < examples.length; i++) {
    const example = examples[i] as Record<string, unknown>;
    if (typeof example !== 'object' || example === null) {
      return `examples[${i}] must be an object`;
    }
    if (typeof example['productName'] !== 'string' || !example['productName'].trim()) {
      return `examples[${i}].productName must be a non-empty string`;
    }
    if (typeof example['customsName'] !== 'string' || !example['customsName'].trim()) {
      return `examples[${i}].customsName must be a non-empty string`;
    }
  }
  return null;
};

export default function CustomsNamePromptPage() {
  const [configText, setConfigText] = useState('');
  const [savedText, setSavedText] = useState('');
  const [version, setVersion] = useState(0);
  const [notYetCreated, setNotYetCreated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const clientRef = useRef<SiteConfigClient | null>(null);

  const getClient = useCallback(() => {
    if (!clientRef.current) {
      clientRef.current = new SiteConfigClient(getAdminApiUrl(AdminApiDomain.SITE_CONFIG));
    }
    return clientRef.current;
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const entry = await getClient().getSiteConfig(SITE_CONFIG_KEY);
        const text = JSON.stringify(entry.config, null, 2);
        setConfigText(text);
        setSavedText(text);
        setVersion(entry.version);
        setNotYetCreated(false);
      } catch (err) {
        const status = (err as { response?: { status?: number } } | null)?.response?.status;
        if (status === 404) {
          const template = JSON.stringify(DEFAULT_CONFIG, null, 2);
          setConfigText(template);
          setSavedText('');
          setNotYetCreated(true);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load config');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [getClient]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    let parsed: unknown;
    try {
      parsed = JSON.parse(configText);
    } catch {
      setError('Invalid JSON — fix the syntax before saving');
      return;
    }
    const validationError = validateConfig(parsed);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    try {
      const entry = await getClient().setSiteConfig(SITE_CONFIG_KEY, parsed as Record<string, unknown>);
      setVersion(entry.version);
      setSavedText(configText);
      setNotYetCreated(false);
      setSuccess('Customs name prompt examples saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = configText !== savedText;

  if (loading) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-3 px-3" style={{ height: 'calc(100vh - 56px)' }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <FileText size={24} />
          <h5 className="mb-0">Customs Name Prompt Examples</h5>
          {notYetCreated ? (
            <Badge bg="secondary">Not Set (Facade Defaults Active)</Badge>
          ) : (
            <Badge bg="primary">Custom</Badge>
          )}
          {hasChanges && (
            <Badge bg="warning" text="dark">
              Unsaved Changes
            </Badge>
          )}
        </div>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !hasChanges}>
          {saving ? <Spinner animation="border" size="sm" /> : <Save size={14} className="me-1" />}
          Save
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <AlertTriangle size={14} className="me-1" />
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-3">
          <CheckCircle size={14} className="me-1" />
          {success}
        </Alert>
      )}

      <Row style={{ height: 'calc(100% - 80px)' }}>
        <Col md={8} className="h-100">
          <Card className="h-100 d-flex flex-column">
            <Card.Header className="d-flex align-items-center justify-content-between py-2">
              <span className="small fw-medium">
                Site Config: <code>{SITE_CONFIG_KEY}</code>
              </span>
              {version > 0 && (
                <span className="d-flex align-items-center gap-1 small text-muted">
                  <Clock size={12} />
                  {new Date(version < 1e12 ? version * 1000 : version).toLocaleString()}
                </span>
              )}
            </Card.Header>
            <Card.Body className="p-0 flex-grow-1" style={{ minHeight: 0 }}>
              <textarea
                aria-label="Customs name prompt JSON configuration"
                value={configText}
                onChange={(e) => setConfigText(e.target.value)}
                className="w-100 h-100 border-0 p-3"
                style={{
                  resize: 'none',
                  fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                  outline: 'none',
                  backgroundColor: '#1e1e1e',
                  color: '#d4d4d4',
                }}
                spellCheck={false}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="h-100" style={{ overflowY: 'auto' }}>
          <Card>
            <Card.Header className="py-2">
              <span className="small fw-medium">Guide</span>
            </Card.Header>
            <Card.Body className="small">
              <p className="mb-2">
                These few-shot examples steer the LLM that generates <strong>customs declaration names</strong> for
                outbound shipments. They are appended to the prompt for every generation.
              </p>
              <h6 className="mt-3 mb-2" style={{ fontSize: '0.8rem' }}>
                Schema
              </h6>
              <ul className="mb-2" style={{ fontSize: '0.8rem', paddingLeft: '1.2rem' }}>
                <li>
                  <code>productName</code> (required) — the store product name
                </li>
                <li>
                  <code>customsName</code> (required) — the desired customs declaration name (2-5 English words)
                </li>
                <li>
                  <code>category</code> (optional) — array of category strings
                </li>
                <li>
                  <code>description</code> (optional) — short product description
                </li>
              </ul>
              <h6 className="mt-3 mb-2" style={{ fontSize: '0.8rem' }}>
                Safety
              </h6>
              <ul className="mb-0" style={{ fontSize: '0.8rem', paddingLeft: '1.2rem' }}>
                <li>The facade validates every example at runtime; invalid entries are silently dropped.</li>
                <li>
                  If the saved config has no valid examples, the facade falls back to its built-in defaults — a broken
                  save cannot take customs name generation down.
                </li>
                <li>Examples whose customs name contains a carrier-restricted word are also dropped automatically.</li>
                <li>Changes apply to newly generated customs names only; existing orders are unaffected.</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
